import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { useSessionStore } from '@/stores/session'
import metaHelpers from '@/utils/meta-helpers'
import { useNotificationStore } from '@/stores/notification'
import { setCookie } from '@/services/cookies'
import { isNetworkOrTimeoutError } from '@/services/api/base'

export const enum TICKETS_ROUTES {
  TICKETS_HOME = 'tickets.home',
  ORDER_NEW = 'orders.new',
  ORDER_CONFIRMATION = 'orders.confirmation',
  NOT_FOUND = 'not-found'
}

export const enum MY_ROUTES {
  ORDERS = 'orders',
  ACCOUNT = 'my-account',
  SECURITY = 'security',
  SIGN_IN = 'signIn',
  SIGN_UP = 'signUp',
  VERIFY_ACCOUNT = 'verify-account',
  VERIFY_EMAIL_CHANGE = 'verify-email-change',
  SIGN_OUT = 'signOut',
  RESET_PASSWORD = 'reset-password',
  SEND_RESET_PASSWORD_INSTRUCTIONS = 'send-reset-password-instructions',
  TRIP_CARDS = 'trip-cards',
  TRIP_CARD_BALANCE_HISTORY = 'trip-card-balance-history',
  TRIP_CARD_TOP_UP_HISTORY = 'trip-card-top-up-history',
  PAYMENT_METHODS = 'payment-methods',
  NOT_FOUND = 'not-found',
  VERIFY_MAGIC_LINK = 'verify-magic-link'
}

const requireAuthenticated = async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    if ((to.name == MY_ROUTES.ORDERS && !to.redirectedFrom) || to.name != MY_ROUTES.ORDERS) {
      setCookie('signInExpired', 'true')
    }
    return { name: MY_ROUTES.SIGN_IN }
  }
}

const redirectIfAuthenticated = (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()

  if (authStore.isAuthenticated) {
    if (to.meta.redirectNotification) {
      notificationStore.notifyWarning(to.meta.redirectNotification as string)
    }
    return { name: MY_ROUTES.ORDERS }
  }
}

const TICKETS_SUBDOMAIN_ROUTES = [
  {
    path: '/orders/new',
    name: TICKETS_ROUTES.ORDER_NEW,
    component: () => import('@/views/tickets/HomeView.vue')
  },
  {
    path: '/orders/:id/confirmation',
    name: TICKETS_ROUTES.ORDER_CONFIRMATION,
    component: () => import('@/views/tickets/OrderPassView.vue'),
    props: true
  },
  {
    path: '/',
    name: TICKETS_ROUTES.TICKETS_HOME,
    component: () => import('@/views/tickets/HomeView.vue')
  },
  {
    path: '/404',
    name: TICKETS_ROUTES.NOT_FOUND,
    component: () => import('@/views/my/NotFoundView.vue')
  },
  /***
   * This is a catch all route to handle when the user is trying to enter an unknown route
   * Needs to be last because the routes are processed in order
   */
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const MY_SUBDOMAIN_ROUTES = [
  {
    path: '/sign_up',
    name: MY_ROUTES.SIGN_UP,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/SignUpView.vue'),
    props: true,
    meta: {
      redirectNotification: 'You cannot signup when you are already signed in.'
    }
  },
  {
    path: '/sign_in',
    name: MY_ROUTES.SIGN_IN,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/SignInView.vue'),
    meta: {
      redirectNotification: 'You are already signed in'
    }
  },
  {
    path: '/account/verify/:token',
    name: MY_ROUTES.VERIFY_ACCOUNT,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/VerifyAccountView.vue'),
    meta: {
      redirectNotification: 'You cannot complete a new signup when you are already signed in'
    }
  },
  {
    path: '/account/verify_email',
    name: MY_ROUTES.VERIFY_EMAIL_CHANGE,
    component: () => import('@/views/my/VerifyEmailChangeView.vue')
  },
  {
    path: '/sign_out',
    name: MY_ROUTES.SIGN_OUT,
    component: () => import('@/views/my/SignOutView.vue')
  },
  {
    path: '/password/reset',
    name: MY_ROUTES.SEND_RESET_PASSWORD_INSTRUCTIONS,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/SendResetPasswordInstructionsView.vue'),
    meta: {
      redirectNotification:
        'Use the Password & Security page to reset your password when already signed in'
    }
  },
  {
    path: '/password/new',
    name: MY_ROUTES.RESET_PASSWORD,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/ResetPasswordView.vue'),
    meta: {
      redirectNotification:
        'Use the Password & Security page to reset your password when already signed in'
    }
  },
  {
    path: '/sessions/magic_link/verify',
    name: MY_ROUTES.VERIFY_MAGIC_LINK,
    beforeEnter: redirectIfAuthenticated,
    component: () => import('@/views/my/VerifyMagicLinkView.vue'),
    meta: {
      redirectNotification: 'Cannot use a magic link when already signed in'
    }
  },
  {
    path: '/',
    component: () => import('@/views/my/account/BaseView.vue'),
    redirect: '/orders',
    children: [
      {
        path: 'orders',
        name: MY_ROUTES.ORDERS,
        beforeEnter: requireAuthenticated,
        component: () => import('@/views/my/account/OrdersView.vue')
      },
      {
        path: '/account',
        name: MY_ROUTES.ACCOUNT,
        beforeEnter: requireAuthenticated,
        component: () => import('@/views/my/account/PersonalPreferencesView.vue')
      },
      {
        path: 'security',
        name: MY_ROUTES.SECURITY,
        beforeEnter: requireAuthenticated,
        component: () => import('@/views/my/account/SecurityView.vue')
      },
      {
        path: 'trip_cards',
        children: [
          {
            path: '',
            name: MY_ROUTES.TRIP_CARDS,
            beforeEnter: [requireAuthenticated],
            component: () => import('@/views/my/account/TripCardsView.vue')
          },
          {
            path: ':id/balance',
            name: MY_ROUTES.TRIP_CARD_BALANCE_HISTORY,
            beforeEnter: [requireAuthenticated],
            component: () => import('@/views/my/account/TripCardBalanceView.vue')
          },
          {
            path: ':id/top_ups',
            name: MY_ROUTES.TRIP_CARD_TOP_UP_HISTORY,
            beforeEnter: [requireAuthenticated],
            component: () => import('@/views/my/account/TripCardTopUpsView.vue')
          }
        ]
      },
      {
        path: 'payment_methods',
        name: MY_ROUTES.PAYMENT_METHODS,
        beforeEnter: requireAuthenticated,
        component: () => import('@/views/my/account/PaymentMethodsView.vue')
      }
    ]
  },
  {
    path: '/404',
    name: MY_ROUTES.NOT_FOUND,
    component: () => import('@/views/my/NotFoundView.vue')
  },

  /***
   * This is a catch all route to handle when the user is trying to enter an unknown route.
   * Needs to be last because the routes are processed in order
   */
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

function getRoutes() {
  const origin = window.location.origin

  if (origin == import.meta.env.VITE_DOMAIN_MY) {
    return MY_SUBDOMAIN_ROUTES
  } else {
    return TICKETS_SUBDOMAIN_ROUTES
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: getRoutes()
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const sessionStore = useSessionStore()
  const customerStore = useCustomerStore()

  authStore.loadTokenFromCookie()
  const pingResult = await sessionStore.ping()

  if (!isNetworkOrTimeoutError(pingResult.errorCode)) {
    authStore.isAuthenticated = pingResult.success

    if (authStore.isAuthenticated) await customerStore.loadFromSessionToken()
  }

  document.title = metaHelpers.getTitle((to.name || '').toString())
  next()
})

router.afterEach(() => {
  const notificationStore = useNotificationStore()
  notificationStore.clearDisplayed()
})

export default router
